import { type ReactNode } from 'react';

import { Card, Checkbox, Radio, Stack, Typography } from '@mui/material';

/**
 * The Attendance Allowance prototype's copy of
 * `components/wrapped/OptionCard`,
 * so Vez's shared component, and every screen that uses it, stays exactly as
 * she built it.
 *
 * What differs: the radio or checkbox carries the card's label, so a screen
 * reader says what choosing it means.
 *
 * These are proposals for the shared component: fold them back, or delete
 * this copy, once she has looked.
 */

// The tappable option card: a Card surface carrying its own radio or checkbox,
// selected state drawn on the surface rather than left to the control alone.
// Onboarding's answer lists are the canonical use, and it is the same object
// wherever a person is picking from a short list of named things.
//
// The surface is llama's, not this file's. `variant='option'` and
// `variant='optionSelected'` already carry the 16px inset, the pointer, the
// 150ms transition and — when selected — the marine border on a lime fill.
// This used to reimplement all of that in a call-site `sx`, which was the same
// values written twice and the kind of thing inspect mode flags coral.
//
// `badge` and `children` are what let a card carry more than a label — a
// timestamp beside the name, a chip flagging its state, a consequence written
// underneath it. All optional; without them this is exactly the onboarding card.
const ClaimOptionCard = ({
  control,
  selected,
  onClick,
  label,
  badge,
  children,
}: {
  control: 'radio' | 'check';
  selected: boolean;
  onClick: () => void;
  label: string;
  /** A chip beside the label, for a state worth flagging on the card itself. */
  badge?: ReactNode;
  /** Anything under the label row, indented to clear the control. */
  children?: ReactNode;
}) => (
  <Card variant={selected ? 'optionSelected' : 'option'} onClick={onClick}>
    <Stack spacing={1}>
      {/* One row, and it does not wrap. It used to carry flexWrap so a badge
          could drop to a second line, but that made the label itself a
          wrappable flex item: any label too long for the row jumped below the
          radio and left the control sitting on its own. The label takes the
          space instead and wraps its own text, which is what a two-line answer
          should do. */}
      <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
        {/* The input carries the card's label, so the control a keyboard or
            screen reader lands on says what choosing it means. */}
        {control === 'radio' ? (
          <Radio
            checked={selected}
            color='primary'
            sx={{ p: 0 }}
            slotProps={{ input: { 'aria-label': label } }}
          />
        ) : (
          <Checkbox
            checked={selected}
            color='primary'
            sx={{ p: 0 }}
            slotProps={{ input: { 'aria-label': label } }}
          />
        )}
        {/* h6, not h5. Under Figma's scale h5 is 20px, which made these read as
            headings rather than answers — the label is a thing you pick, not a
            section title. h6 is Work Sans Medium 16, which is what h5 used to
            be before the scale moved. */}
        <Typography sx={{ flex: 1 }} variant='h6'>
          {label}
        </Typography>
        {badge}
      </Stack>
      {/* Indented past the control so the extra line reads as belonging to this
          card's label rather than to the card itself. */}
      {children && <Stack sx={{ pl: 4 }}>{children}</Stack>}
    </Stack>
  </Card>
);

ClaimOptionCard.displayName = 'ClaimOptionCard';
export default ClaimOptionCard;
